import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { colors, fonts, size } from "../../ColorFont/color";
import { url } from "../../ConstUrl/Url";
import { CalendarToday, Delete, Edit, Upload } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ManageDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [bannerImages, setBannerImages] = useState([]);
  const [uploadImage, setUploadImage] = useState(false);
  const [editingImageId, setEditingImageId] = useState(null);
  const [updatedImage, setUpdatedImage] = useState(null);
  const [dimensionError, setDimensionError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    fetchBannerImages();
  }, []);

  const fetchBannerImages = () => {
    setIsLoading(true);
    fetch(`${url}/api/get-banner-images`)
      .then((response) => response.json())
      .then((data) => {
        setBannerImages(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    
    // Check image dimensions
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
        if (img.naturalWidth !== 1280 || img.naturalHeight !== 720) {
          setDimensionError(true);
        } else {
          setDimensionError(false);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (!selectedFile || dimensionError) return;
    
    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("name", fileName);

    fetch(`${url}/api/upload-banner-images`, {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then(() => {
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
        fetchBannerImages();
        setUploadImage(false);
        setSelectedFile(null);
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  };

  const handleDeleteImage = (id) => {
    setIsLoading(true);
    fetch(`${url}/api/delete-banner-images/${id}`, {
      method: "DELETE",
    })
      .then(() => {
        setBannerImages(bannerImages.filter((image) => image._id !== id));
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  };

  return (
    <Box sx={{ margin: "14px" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: 4,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: fonts.poppins,
            fontSize: isMobile ? size.mobileTitle : size.title,
            fontWeight: 700,
            color: colors.primary_dark,
            lineHeight: 1.2,
          }}
        >
          Manage Dashboard
        </Typography>

        <Button
          variant="contained"
          onClick={() => setUploadImage(true)}
          sx={{
            fontFamily: fonts.poppins,
            background: colors.primary_dark,
            padding: "8px 20px",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
            },
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            width: isMobile ? "100%" : "auto",
            textTransform: "none",
          }}
        >
          <Upload sx={{ marginRight: 1, fontSize: "1.2rem" }} />
          Upload New Image
        </Button>
      </Box>

      {uploadSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Image uploaded successfully!
        </Alert>
      )}

      {/* Image Grid */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 3,
            marginBottom: 4,
          }}
        >
          {bannerImages.map((image) => (
            <Paper
              key={image._id}
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 12px 24px rgba(0, 0, 0, 0.15)",
                },
                position: "relative",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: "200px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={`${url}/${image.url}`}
                  alt={image.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.5s ease",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    padding: 1,
                    background: "rgba(0, 0, 0, 0.5)",
                    borderRadius: "0 0 0 16px",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <IconButton
                    onClick={() => handleDeleteImage(image._id)}
                    sx={{
                      color: "white",
                      background: colors.primary,
                      "&:hover": { background: colors.primary_dark },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ padding: 3 }}>
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontSize: size.subtitle,
                    fontWeight: 600,
                    color: colors.primary_dark,
                    marginBottom: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {image.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontSize: size.small,
                    color: colors.text,
                  }}
                >
                  Dimensions: 1280×720
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadImage}
        onClose={() => {
          setUploadImage(false);
          setSelectedFile(null);
          setDimensionError(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(12px)",
            padding: isMobile ? 2 : 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 700,
            color: colors.primary_dark,
            fontSize: isMobile ? size.mobileTitle : size.title,
            textAlign: "center",
            paddingBottom: 2,
          }}
        >
          Upload New Image
        </DialogTitle>

        <DialogContent>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleUpload();
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              padding: 2,
            }}
          >
            {/* Drag and Drop Area */}
            <Box
              sx={{
                width: "100%",
                border: `2px dashed ${dimensionError ? colors.error : colors.primary}`,
                borderRadius: 2,
                padding: 4,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(0, 0, 0, 0.02)",
                  borderColor: dimensionError ? colors.error : colors.primary_dark,
                },
              }}
              onClick={() => document.getElementById("file-input").click()}
            >
              <CloudUploadIcon
                sx={{ fontSize: "3rem", color: dimensionError ? colors.error : colors.primary, mb: 1 }}
              />
              <Typography
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: size.subtitle,
                  fontWeight: 600,
                  color: dimensionError ? colors.error : colors.primary_dark,
                  mb: 1,
                }}
              >
                Drag & Drop your image here
              </Typography>
              <Typography
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: size.formLabel,
                  color: dimensionError ? colors.error : colors.textSecondary,
                  mb: 2,
                }}
              >
                {dimensionError 
                  ? "Image must be exactly 1280×720 pixels" 
                  : "or click to browse files (1280×720 required)"}
              </Typography>
              <input
                id="file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <Button
                component="span"
                variant="contained"
                startIcon={<Upload />}
                sx={{
                  background: dimensionError ? colors.error : colors.button,
                  "&:hover": { 
                    background: dimensionError ? colors.error_dark : colors.button_hover 
                  },
                }}
              >
                Select File
              </Button>
            </Box>

            {/* Preview Section */}
            {selectedFile && (
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontSize: size.subtitle,
                    fontWeight: 600,
                    color: colors.primary_dark,
                    mb: 1,
                  }}
                >
                  Preview
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 2,
                    padding: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <img
                    src={URL.createObjectURL(selectedFile)}
                    alt="Preview"
                    style={{
                      width: "120px",
                      height: "67.5px", // 16:9 aspect ratio
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: dimensionError ? `2px solid ${colors.error}` : "none"
                    }}
                    onLoad={() => URL.revokeObjectURL(selectedFile)}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 500,
                        fontSize: size.formLabel,
                      }}
                    >
                      {fileName}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: fonts.poppins,
                        fontSize: size.small,
                        color: colors.text,
                      }}
                    >
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: fonts.poppins,
                        fontSize: size.small,
                        color: dimensionError ? colors.error : colors.success,
                        mt: 1,
                      }}
                    >
                      Dimensions: {imageDimensions.width}×{imageDimensions.height}
                    </Typography>
                    {dimensionError && (
                      <Typography
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: size.small,
                          color: colors.error,
                          mt: 1,
                        }}
                      >
                        Error: Image must be exactly 1280×720 pixels
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 3, justifyContent: "center" }}>
          <Button
            onClick={() => {
              setUploadImage(false);
              setSelectedFile(null);
              setDimensionError(false);
            }}
            variant="outlined"
            sx={{
              fontFamily: fonts.poppins,
              color: colors.primary_dark,
              borderColor: colors.primary_dark,
              minWidth: "120px",
              "&:hover": {
                borderColor: colors.primary,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || dimensionError || isLoading}
            sx={{
              fontFamily: fonts.poppins,
              background: dimensionError 
                ? colors.error 
                : `linear-gradient(135deg, ${colors.button} 0%, ${colors.button_hover} 100%)`,
              minWidth: "120px",
              color: "white",
              "&:disabled": {
                background: dimensionError ? colors.error_light : "#eee",
                color: dimensionError ? "white" : "#999",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : dimensionError ? (
              "Invalid Dimensions"
            ) : (
              "Upload"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDashboard;